import { Dispatch } from 'redux'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import {
  createTradeNote,
  loadTradeNotesByMasterTrade,
  loadTradeNotesBySubTrade,
  markTradeNoteRead,
  markTradeNotesReadBulk,
  reopenTradeNote,
} from 'app/state/ducks/trade-notes/thunks'
import { TradeNoteItem } from 'app/state/ducks/trade-notes/types'
import { loadTradeActivity } from 'app/state/ducks/trade-detail/thunks'

/**
 * Ledger-backed actions for the trade Activity Stream (ADR_LOS-028).
 *
 * The frontend never talks to the ledger directly — these go through the backend HTTP API,
 * which owns the DAML command submission.
 *
 * There is no edit and no delete action here, by decision rather than omission: Phase 1 notes
 * are create-only while the edit/delete product question is open.
 */

export const addTradeNote = async (
  subTradeId: string,
  body: string,
  dispatch: Dispatch<any>,
) => {
  const result: any = await dispatch(createTradeNote(subTradeId, body))
  if (!result?.success) {
    ErrorNotification({
      title: 'Note Not Added',
      message: result?.payload || 'Action Failed',
    })
    return null
  }
  SuccessNotification({
    title: 'Note Added',
    message: 'Your note is now visible to the other parties on this trade',
  })
  return result.payload
}

/**
 * "All sub-trades (N)" in the composer: a note is a per-sub-trade contract, so posting to all
 * means one create per sub-trade — each with its own recipients, which is the point. Sequential
 * on purpose: parallel submission of N ledger commands from one click invites contention for no
 * user-visible gain at Phase 1 volumes.
 *
 * Partial failure is reported honestly and the composer keeps the text: some sub-trades now
 * carry the note and some do not, and a blind retry would duplicate it on the ones that
 * succeeded — so the user is told to check the stream first.
 */
export const addTradeNoteToAll = async (
  subTradeIds: string[],
  body: string,
  dispatch: Dispatch<any>,
): Promise<boolean> => {
  const failures: string[] = []
  for (const subTradeId of subTradeIds) {
    const result: any = await dispatch(createTradeNote(subTradeId, body))
    if (!result?.success) {
      failures.push(subTradeId)
    }
  }
  if (failures.length === 0) {
    SuccessNotification({
      title: 'Note Added',
      message: `Your note is now on all ${subTradeIds.length} sub-trades of this trade`,
    })
    return true
  }
  ErrorNotification({
    title: 'Note Not Added to Every Sub-trade',
    message: `Added to ${subTradeIds.length - failures.length} of ${subTradeIds.length} sub-trades. Check the stream before retrying — retrying posts the note again on the ones that succeeded.`,
  })
  return false
}

/**
 * The Activity tab's one read (LOS-7095) — the notes with the trade, the header and the
 * composer's sub trades — with the stream's failure handling. The page shell runs it when the
 * tab opens, and `refreshTrade` runs it `fresh` after a write on the tab (a write can change
 * the header's warnings).
 *
 * A failed read keeps the last-known-good notes on screen (the stores are untouched) but must
 * never do so SILENTLY — whether the tab is opening or the user just asked for current data,
 * they would otherwise believe they have it. Same toast the SP drawer shows for the same
 * failure. A read overtaken by a newer one is not a failure: the newer one lands.
 */
export const loadMasterTradeStream = async (
  masterTradeId: string,
  dispatch: Dispatch<any>,
  options: { fresh?: boolean } = {},
) => {
  const result: any = await dispatch(loadTradeActivity(masterTradeId, options))
  if (result && !result.success && !result.stale) {
    ErrorNotification({
      title: 'Activity Stream Unavailable',
      message: result.payload || 'Failed to load the activity stream',
    })
  }
  return result
}

/**
 * The stream's Refresh button: the notes alone (review on #8472). Picking up new notes needs
 * only the narrow notes read, not the tab's whole statement again; a stale copy from it is
 * the merge rule's job (LOS-7094). Same failure toast.
 */
export const refreshMasterTradeStream = async (
  masterTradeId: string,
  dispatch: Dispatch<any>,
) => {
  const result: any = await dispatch(loadTradeNotesByMasterTrade(masterTradeId))
  if (!result?.success) {
    ErrorNotification({
      title: 'Activity Stream Unavailable',
      message: result?.payload || 'Failed to load the activity stream',
    })
  }
}

export const refreshSubTradeStream = async (
  subTradeId: string,
  dispatch: Dispatch<any>,
) => {
  const result: any = await dispatch(loadTradeNotesBySubTrade(subTradeId))
  if (!result?.success) {
    ErrorNotification({
      title: 'Activity Stream Unavailable',
      message: result?.payload || 'Failed to load the activity stream',
    })
  }
}

/**
 * "Mark as actioned" (LOS-6987): waits for the ledger's receipt rather than patching
 * optimistically — the button shows a loading state meanwhile. FIRST CLICK WINS on the ledger,
 * so the response can name another reader, and the "Actioned by" line must never show a name
 * it then has to take back. The thunk upserts the returned item, which swaps the button for
 * that line. Resolves true on success; a failure leaves the note unactioned, with a toast.
 */
export const markNoteRead = async (
  item: TradeNoteItem,
  dispatch: Dispatch<any>,
): Promise<boolean> => {
  const result: any = await dispatch(markTradeNoteRead(item.id))
  if (!result?.success) {
    ErrorNotification({
      title: 'Note Not Marked as Actioned',
      message: result?.payload || 'Action Failed',
    })
    return false
  }
  return true
}

/**
 * "Mark all N as actioned" (LOS-6989). No optimistic patch, for the same reason as a single
 * mark: the "Actioned by" lines must name whoever's receipt actually stands. Notes that could
 * not be marked stay unactioned — with their own buttons, so they can be retried — and one
 * toast says how many. Resolves true when every note was marked.
 */
export const markAllNotesActioned = async (
  masterTradeId: string,
  items: TradeNoteItem[],
  dispatch: Dispatch<any>,
): Promise<boolean> => {
  const result: any = await dispatch(
    markTradeNotesReadBulk(
      masterTradeId,
      items.map(item => item.id),
    ),
  )
  if (!result?.success) {
    ErrorNotification({
      title: 'Notes Not Marked as Actioned',
      message: result?.payload || 'Action Failed',
    })
    return false
  }
  const failedCount = result.payload?.failed?.length ?? 0
  if (failedCount > 0) {
    ErrorNotification({
      title: 'Some Notes Not Marked as Actioned',
      message: `${failedCount} of ${items.length} notes could not be marked as actioned. They are still unactioned — try them again.`,
    })
    return false
  }
  return true
}

/**
 * Undo "Mark as actioned" (LOS-6988). Like marking, it waits for the ledger rather than patching
 * optimistically: the "Reopened by" line must name whoever's undo actually stands (two people
 * undoing at once both get the winner back). Resolves true once the reopened item is in the store.
 */
export const reopenNote = async (
  item: TradeNoteItem,
  dispatch: Dispatch<any>,
): Promise<boolean> => {
  const result: any = await dispatch(reopenTradeNote(item.id))
  if (!result?.success) {
    ErrorNotification({
      title: 'Action Not Undone',
      message: result?.payload || 'Action Failed',
    })
    return false
  }
  return true
}
